import classNames from 'classnames';
import React, { FC, PropsWithChildren, useRef } from 'react';

import { CLICK_OUTSIDE_IGNORE_ATTR_NAME } from '../ClickOutside/ClickOutside.constants';
import { Portal } from '../Portal/Portal';
import { useCatchFocusInElement } from '../hooks/useCatchFocusInElement/useCatchFocusInElement';
import styles from './OverlayingPopup.styles.m.css';

export type TOverlayingPopupTheme = 'standard' | 'brand';

interface IOverlayingPopupProps {
  overlayClassName?: string;
  popupClassName?: string;
  className?: string;
  isIgnoreClickOutside?: boolean;
  isOpened?: boolean;
  dataTest?: string;
  onClose?(): void;
  theme?: TOverlayingPopupTheme;
}

export const OverlayingPopup: FC<PropsWithChildren<IOverlayingPopupProps>> = ({
  children,
  onClose,
  className = undefined,
  popupClassName,
  overlayClassName,
  isIgnoreClickOutside = false,
  isOpened,
  dataTest,
  theme = 'standard',
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useCatchFocusInElement(rootRef);

  if (!isOpened) {
    return null;
  }

  const containerClassName = classNames(styles.container, className, styles[`theme-${theme}`]);

  return (
    <Portal>
      <div
        className={containerClassName}
        role="dialog"
        ref={rootRef}
        data-test={dataTest}
        {...{ [CLICK_OUTSIDE_IGNORE_ATTR_NAME]: isIgnoreClickOutside }}
      >
        <div
          className={classNames(styles.overlay, overlayClassName)}
          role="button"
          tabIndex={0}
          onClick={!isIgnoreClickOutside ? onClose : undefined}
        />
        <div className={classNames(styles.popup, popupClassName)}>{children}</div>
      </div>
    </Portal>
  );
};
